import type { SessionUser } from '@/components/auth/user-provider';
import { defaultEntity } from '@/features/tenancy/context-navigation';
import { tenantPath, tenantSlugFromPath } from '@/features/tenancy/urls';

export interface LayoutAuthority {
  entityId: string;
  canManageTenant: boolean;
  canManageEntity: boolean;
  navigationEntityUnlocked: boolean;
  homeEntityUnlocked: boolean;
}

export type LayoutManageTarget = {
  scope: 'platform' | 'tenant' | 'entity';
  to: string;
};

export interface LayoutManageTargets {
  navigation: LayoutManageTarget | null;
  home: LayoutManageTarget | null;
}

/** Resolve links from current server-derived authority; Managed mode never grants access. */
export function resolveLayoutManageTargets(
  user: Pick<SessionUser, 'isPlatformAdmin' | 'viewAsRole' | 'tenants'> | null,
  authority: LayoutAuthority | null,
  pathname: string
): LayoutManageTargets {
  const none = { navigation: null, home: null };
  if (!user || user.viewAsRole) return none;
  const platform: LayoutManageTargets = user.isPlatformAdmin ? {
    navigation: { scope: 'platform', to: '/platform-admin#platform-navigation' },
    home: { scope: 'platform', to: '/platform-admin#platform-home' }
  } : none;
  if (pathname.startsWith('/platform-admin')) return platform;

  const slug = tenantSlugFromPath(pathname);
  const entity = slug
    ? user.tenants.find((item) => item.slug === slug)
    : defaultEntity(user.tenants);
  if (!entity || entity.status !== 'active' || authority?.entityId !== entity.id) return platform;

  const target = (kind: 'navigation' | 'home', scope: 'tenant' | 'entity'): LayoutManageTarget => ({
    scope,
    to: tenantPath(entity.slug, `tenant/entity/branding#${scope}-${kind}-layout`)
  });
  return {
    navigation: authority.canManageTenant
      ? target('navigation', 'tenant')
      : authority.canManageEntity && authority.navigationEntityUnlocked
        ? target('navigation', 'entity')
        : platform.navigation,
    home: authority.canManageTenant
      ? target('home', 'tenant')
      : authority.canManageEntity && authority.homeEntityUnlocked
        ? target('home', 'entity')
        : platform.home
  };
}
